import { Controller } from "@hotwired/stimulus";
import "rhino-editor";
import "rhino-editor/exports/styles/trix.css";
import "./component.scss";
import Link from "@tiptap/extension-link";
import Mention from "@tiptap/extension-mention";
import { buildMentionSuggestion } from "../../../helpers/mention_suggestion";
import { mergeAttributes } from "@tiptap/core";

const ATTACHMENT_NODE_TYPES = new Set([
  "attachment-figure",
  "previewable-attachment-figure",
]);

export default class extends Controller {
  static targets = ["editor", "counter", "counterText"];

  static values = {
    placeholder: String,
    autofocus: { type: Boolean, default: false },
    maxlength: { type: Number, default: null },
    acceptedAttachmentTypes: { type: Array, default: [] },
    attachmentNotAllowed: { type: String, default: "" },
    communityId: { type: String, default: "" },
  };

  initialize() {
    this.isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    this.beforeIntialize = this.beforeIntialize.bind(this);
    this.onInitialized = this.onInitialized.bind(this);

    this.onFileAccept = this.onFileAccept.bind(this);
    this.onAttachmentAdd = this.onAttachmentAdd.bind(this);
    this.onToolbarMousedown = this.onToolbarMousedown.bind(this);
    this.onAttachmentDeletePointerdown =
      this.onAttachmentDeletePointerdown.bind(this);

    this.updateCharacterCount = this.updateCharacterCount.bind(this);
  }

  connect() {
    // Use Stimulus target if available, fallback to querySelector
    this.rhinoEditor = this.hasEditorTarget
      ? this.editorTarget
      : this.element.querySelector("rhino-editor");

    this.rhinoEditor?.addEventListener("mousedown", this.onToolbarMousedown);
    this.rhinoEditor?.addEventListener(
      "pointerdown",
      this.onAttachmentDeletePointerdown,
    );

    document.addEventListener("rhino-before-initialize", this.beforeIntialize);
    document.addEventListener("rhino-initialize", this.onInitialized);
    document.addEventListener("rhino-file-accept", this.onFileAccept);
    document.addEventListener("rhino-attachment-add", this.onAttachmentAdd);

    if (this.maxlengthValue) {
      document.addEventListener("rhino-change", this.updateCharacterCount);
    }
  }

  disconnect() {
    this.rhinoEditor?.removeEventListener("mousedown", this.onToolbarMousedown);
    this.rhinoEditor?.removeEventListener(
      "pointerdown",
      this.onAttachmentDeletePointerdown,
    );

    document.removeEventListener(
      "rhino-before-initialize",
      this.beforeIntialize,
    );
    document.removeEventListener("rhino-initialize", this.onInitialized);
    document.removeEventListener("rhino-file-accept", this.onFileAccept);
    document.removeEventListener("rhino-attachment-add", this.onAttachmentAdd);

    if (this.maxlengthValue) {
      document.removeEventListener("rhino-change", this.updateCharacterCount);
    }
  }

  // Keep the caret in the editor while a toolbar button is pressed.
  //
  // In `simple` mode the main toolbar is hidden and the bubble menu is the only
  // formatting affordance. Rhino tears that menu down as soon as the editor
  // blurs, and it only survives the blur because the pressed button itself is
  // `:focus-within`. Browsers that do not focus a button on mousedown — Safari
  // and Firefox on macOS — therefore lose the menu between mousedown and
  // mouseup, so the click never reaches the button and no mark is applied.
  // (cmd+b is unaffected: it never blurs the editor.)
  //
  // Suppressing the default focus shift keeps the editor focused, so the menu
  // stays put and the button's own click handler runs. The buttons re-focus the
  // editor themselves via `chain().focus()`, so nothing is lost.
  onToolbarMousedown(event) {
    const isToolbarButton = event
      .composedPath()
      .some((node) => node?.matches?.("[data-role='toolbar-item']"));

    if (isToolbarButton) event.preventDefault();
  }

  // Leave the editor once an attachment has been removed.
  //
  // Rhino removes an attachment on pointerdown of its × button. The selection
  // then falls into the next attachment, which slides up and shows its own ×
  // exactly under the pointer — so a double-click (or a quick second click)
  // deletes that attachment as well. Without editor focus no × is shown until
  // the creator deliberately selects an attachment again.
  onAttachmentDeletePointerdown(event) {
    const isDeleteButton = event
      .composedPath()
      .some((node) => node?.matches?.("[part~='delete-button']"));

    if (isDeleteButton) this.rhinoEditor?.editor?.commands.blur();
  }

  beforeIntialize(event) {
    // Only handle events from our own editor
    if (!this.isOwnEditor(event.target)) return;

    // Rhino's bundled normalizer interpolates stored captions through
    // insertAdjacentHTML. Keep its DOM-shaping behavior, but treat captions as
    // untrusted text before the editor parses the saved input.
    this.rhinoEditor.normalizeDOM = this.normalizeRhinoDOM.bind(this);

    // Create custom Link extension with correct mark behavior
    const CustomLink = Link.extend({
      inclusive: false, // Cursor exits link at the end
      exitable: true, // Link is exitable at node end
    }).configure({
      HTMLAttributes: {
        target: "_blank",
        rel: "nofollow ugc noopener noreferrer",
      },
      linkOnPaste: true,
      autolink: true,
      openOnClick: true,
      defaultProtocol: "https",
    });

    // Rebuild the extension list, dropping the defaults we override.
    const extensions = this.rhinoEditor.extensions.filter(
      (ext) => ext.name !== "link" && ext.name !== "mention",
    );

    // Links: available to every member, regardless of role.
    extensions.push(CustomLink);

    // @mentions: available to anyone who can post in a community context.
    if (this.communityIdValue) {
      extensions.push(
        Mention.configure({
          HTMLAttributes: { class: "mention" },
          renderText({ node }) {
            return `@${node.attrs.label ?? node.attrs.id}`;
          },
          // Serialize TipTap's standard data attributes. data-id carries the
          // mentioned user's id — the backend resolves notification recipients
          // from it (MentionExtractionService) and TipTap's default parseHTML
          // restores full mention nodes when a post is edited.
          renderHTML({ options, node }) {
            return [
              "span",
              mergeAttributes(options.HTMLAttributes, {
                "data-type": "mention",
                "data-id": node.attrs.id,
                "data-label": node.attrs.label,
              }),
              `@${node.attrs.label ?? node.attrs.id}`,
            ];
          },
          suggestion: buildMentionSuggestion({
            communityId: this.communityIdValue,
            appendTo: () => this.element.closest("dialog") || document.body,
          }),
        }),
      );
    }

    this.rhinoEditor.extensions = extensions;

    this.rhinoEditor.starterKitOptions = {
      ...this.rhinoEditor.starterKitOptions,
      rhinoLink: false,
      strike: false,
      code: false,
      history: false,
      codeBlock: false,
      heading: false,
      blockquote: false,
      increaseIndentation: false,
      decreaseIndentation: false,
      rhinoCodemarkPlugin: false,
      rhinoFigcaption: false,
      rhinoAttachment: this.allowAttachments(),
      rhinoGallery: this.allowAttachments(),
      rhinoPlaceholder: {
        placeholder: this.placeholderValue,
      },
    };

    if (this.isMobile) {
      this.rhinoEditor.disableStarterKitOptions("rhinoBubbleMenu");
    }

    this.rhinoEditor.accept = this.acceptedAttachmentTypesValue;
  }

  normalizeRhinoDOM(inputElement, parser = new DOMParser()) {
    if (inputElement == null || inputElement.value == null) return;

    const doc = parser.parseFromString(inputElement.value, "text/html");
    const figures = [...doc.querySelectorAll("figure[data-trix-attachment]")];
    const figuresWithoutCaptions = figures.filter(
      (figure) => figure.querySelector("figcaption") == null,
    );

    doc.querySelectorAll("div > figure:first-child").forEach((figure) => {
      figure.parentElement?.classList.add("attachment-gallery");
    });

    figuresWithoutCaptions.forEach((figure) => {
      const attributes = figure.getAttribute("data-trix-attributes");
      if (!attributes) return;

      try {
        const { caption } = JSON.parse(attributes);
        if (!caption) return;

        const figcaption = doc.createElement("figcaption");
        figcaption.classList.add("attachment__caption");
        figcaption.textContent = caption;
        figure.appendChild(figcaption);
      } catch {
        // A malformed legacy attribute must not prevent the editor from opening.
      }
    });

    doc
      .querySelectorAll(
        "figure :not(.attachment__caption--edited) .attachment__name",
      )
      .forEach((element) => {
        if (element.textContent?.includes(" · ") === false) return;
        element.insertAdjacentText("beforeend", " · ");
      });

    doc
      .querySelectorAll(":scope > p > br:not(.ProseMirror-trailingBreak)")
      .forEach((element) => element.remove());

    const body = doc.querySelector("body");
    if (body) inputElement.value = body.innerHTML;
  }

  onInitialized(event) {
    // Only handle events from our own editor
    if (!this.isOwnEditor(event.target)) return;

    const editor = this.rhinoEditor?.editor;
    const textContent = editor?.getText() || "";

    // On Mobile Safari, whitespace-only paragraphs can prevent the placeholder
    // from appearing. Clear those, but preserve attachment-only content: Rhino
    // attachment nodes do not contribute to getText().
    if (textContent.trim() === "" && !this.hasAttachmentNodes(editor)) {
      editor?.commands.clearContent();
    } else {
      this.stripEmptyParagraphBreaks();
    }

    // Focus immediately without setTimeout to preserve iOS user-gesture chain.
    // iOS Safari only opens keyboard if focus happens within a user gesture.
    if (this.autofocusValue) {
      this.rhinoEditor?.editor?.commands.focus();
    }

    // Initialize character counter
    if (this.maxlengthValue && this.hasCounterTextTarget) {
      this.updateCharacterCount();
    }
  }

  hasAttachmentNodes(editor) {
    if (!editor) return false;

    let hasAttachments = false;

    editor.state.doc.descendants((node) => {
      if (ATTACHMENT_NODE_TYPES.has(node.type.name)) {
        hasAttachments = true;
        return false;
      }
    });

    return hasAttachments;
  }

  // Remove the stray hard break rhino-editor inserts into empty paragraphs.
  //
  // A saved blank line is stored as `<p><br></p>` (rhino's blank-line marker).
  // When the editor initializes with that content, TipTap's initial parse turns
  // the empty paragraph into a paragraph containing a real hardBreak node — which
  // then renders an *additional* ProseMirror-trailingBreak, doubling the blank
  // line on every edit. (Paragraphs typed live during editing don't hit this; only
  // ones present at load do.) We strip the lone hardBreak so the paragraph becomes
  // a genuine empty paragraph again, matching how it renders on first creation.
  stripEmptyParagraphBreaks() {
    const editor = this.rhinoEditor?.editor;
    if (!editor) return;

    const { state } = editor;
    const positions = [];
    state.doc.descendants((node, pos) => {
      if (
        node.type.name === "paragraph" &&
        node.childCount === 1 &&
        node.firstChild.type.name === "hardBreak"
      ) {
        // Position of the hardBreak node itself (paragraph start + 1).
        positions.push(pos + 1);
      }
    });

    if (positions.length === 0) return;

    const tr = state.tr;
    // Delete back-to-front so earlier positions stay valid. hardBreak is a leaf
    // node of size 1. Keep it out of the undo history.
    positions
      .reverse()
      .forEach((pos) => tr.delete(pos, pos + 1));
    tr.setMeta("addToHistory", false);
    editor.view.dispatch(tr);
  }

  // Check if the event target is our editor
  isOwnEditor(target) {
    return target === this.rhinoEditor || this.element.contains(target);
  }

  allowAttachments() {
    return !this.acceptedAttachmentTypesValue.includes(
      this.attachmentNotAllowedValue,
    );
  }

  onAttachmentAdd(event) {
    // Only handle events from our own editor
    if (!this.isOwnEditor(event.target)) return;
    // Placeholder for future attachment handling
  }

  onFileAccept(event) {
    // Only handle events from our own editor
    if (!this.isOwnEditor(event.target)) return;

    // only allowed types
    let fileType = event.file.type;
    let acceptedTypes = this.acceptedAttachmentTypesValue;

    if (acceptedTypes.length > 0) {
      if (!this.allowAttachments()) {
        event.preventDefault();
        return;
      }

      const fileExtension = event.file.name
        ? "." + event.file.name.split(".").pop().toLowerCase()
        : "";
      const mimeMatch =
        acceptedTypes.includes(fileType) ||
        acceptedTypes.includes(fileType.split("/")[0] + "/*");
      const extensionMatch =
        fileExtension && acceptedTypes.includes(fileExtension);

      if (!mimeMatch && !extensionMatch) {
        event.preventDefault();
        return;
      }
    } else {
      // if no types specified, reject all
      event.preventDefault();
      return;
    }
  }

  updateCharacterCount(event) {
    // Only handle events from our own editor (if event exists)
    if (event && !this.isOwnEditor(event.target)) return;

    if (!this.maxlengthValue || !this.hasCounterTextTarget) return;

    const textContent = this.rhinoEditor?.editor?.getText() || "";
    const currentLength = textContent.trim().length;

    this.counterTextTarget.textContent = `${currentLength} / ${this.maxlengthValue}`;

    // Add warning class if exceeded
    const counterElement = this.counterTarget;
    const baseClass = Array.from(counterElement.classList).find((cls) =>
      cls.includes("form__character-counter"),
    );

    if (baseClass) {
      const exceededClass = `${baseClass}--exceeded`;

      if (currentLength > this.maxlengthValue) {
        counterElement.classList.add(exceededClass);
      } else {
        counterElement.classList.remove(exceededClass);
      }
    }
  }
}
